import { fetchJSON, HttpError } from '../utils/fetchClient';
import { AuthService, authService } from './AuthService';
import type { WeatherStation, WeatherStationAPIResponse } from '../types/weatherStation';
import { parseAdmin } from '../types/weatherStation';
import { getStationTier } from '../utils/stationTier';
import { GeocodingService } from './GeocodingService';
import { APIResponseValidator } from './apiResponseValidator';
import { createLogger } from '../utils/logger';

const log = createLogger('WeatherStationAPI');

export const ErrorType = {
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  STATION_NOT_FOUND: 'STATION_NOT_FOUND',
  INVALID_RESPONSE: 'INVALID_RESPONSE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

export class APIError extends Error {
  type: ErrorType;
  code?: string;
  correlationId?: string;
  originalError?: Error;

  constructor(
    type: ErrorType,
    message: string,
    originalError?: Error,
    code?: string,
    correlationId?: string
  ) {
    super(message);
    this.name = 'APIError';
    this.type = type;
    this.code = code;
    this.correlationId = correlationId;
    this.originalError = originalError;
  }
}

export class WeatherStationAPI {
  private authService: AuthService;
  private baseURL: string;
  private invalidCoordinatesCount: Map<string, number> = new Map();
  private cacheService: any;
  private cacheServicePromise: Promise<any> | null = null;

  constructor(authService: AuthService) {
    this.authService = authService;
    
    // Initialize cache service (lazy import to avoid circular dependencies)
    this.cacheServicePromise = import('./StationCacheService').then(async module => {
      this.cacheService = module.StationCacheService.getInstance();
      log.info('StationCacheService loaded (not auto-initializing)');
      return this.cacheService;
    }).catch(error => {
      log.warn('Failed to load StationCacheService:', error);
      return null;
    });

    // ponytail: config check removed — singleton is created at module load
    // before ConfigLoader.load() resolves. BaseURL doesn't depend on config.
    // Always use window.location.origin so requests route through the Vite proxy.
    // The proxy forwards /api/* to DEV_BACKEND_PORT — hardcoding 3001 breaks multi-worktree setups.
    this.baseURL = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001';
    log.info(`Using base URL: ${this.baseURL}`);
  }

  // ponytail: three thin helpers replace three AxiosInstance + interceptor combos
  /**
   * Fetch with weather API auth header + timeout via AbortController.
   */
  private async authFetch<T>(path: string, options?: RequestInit & { timeout?: number; params?: Record<string, string | number> }): Promise<T> {
    const { timeout = 10000, params, ...fetchOpts } = options ?? {};
    const url = this.buildURL(`${this.baseURL}/api/weather${path}`, params);
    const token = await this.getTokenOrThrow(() => this.authService.getAccessToken());
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetchJSON<T>(url, {
        ...fetchOpts,
        signal: controller.signal,
        headers: { ...fetchOpts.headers as Record<string, string>, Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      throw this.mapFetchError(error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Fetch with observations API auth header.
   */
  private async observationsAuthFetch<T>(path: string, options?: RequestInit & { timeout?: number; params?: Record<string, string | number> }): Promise<T> {
    const { timeout = 120000, params, ...fetchOpts } = options ?? {};
    const url = this.buildURL(`${this.baseURL}/api/observations${path}`, params);
    const token = await this.getTokenOrThrow(() => this.authService.getObservationsAccessToken());
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetchJSON<T>(url, {
        ...fetchOpts,
        signal: controller.signal,
        headers: { ...fetchOpts.headers as Record<string, string>, Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      throw this.mapFetchError(error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Fetch with station manager API auth header.
   */
  private async stationManagerAuthFetch<T>(path: string, options?: RequestInit & { timeout?: number; params?: Record<string, string | number> }): Promise<T> {
    const { timeout = 30000, params, ...fetchOpts } = options ?? {};
    const url = this.buildURL(`${this.baseURL}/api/station-manager${path}`, params);
    const token = await this.getTokenOrThrow(() => this.authService.getStationManagerAccessToken());
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetchJSON<T>(url, {
        ...fetchOpts,
        signal: controller.signal,
        headers: { ...fetchOpts.headers as Record<string, string>, Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      throw this.mapFetchError(error);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Unauthenticated fetch with timeout (for endpoints that don't need OAuth).
   */
  private async plainFetch<T>(url: string, options?: RequestInit & { timeout?: number; params?: Record<string, string | number> }): Promise<T> {
    const { timeout = 30000, params, ...fetchOpts } = options ?? {};
    const fullUrl = this.buildURL(url, params);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
      return await fetchJSON<T>(fullUrl, { ...fetchOpts, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private buildURL(base: string, params?: Record<string, string | number>): string {
    if (!params) return base;
    const url = new URL(base);
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== null) url.searchParams.set(key, String(val));
    }
    return url.toString();
  }

  private async getTokenOrThrow(getter: () => Promise<string>): Promise<string> {
    try {
      return await getter();
    } catch (error) {
      throw new APIError(
        ErrorType.AUTHENTICATION_FAILED,
        'Failed to obtain access token',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Map fetch/HttpError to APIError (replaces handleError + AxiosError checks).
   */
  private mapFetchError(error: unknown): APIError {
    if (error instanceof APIError) return error;

    if (error instanceof HttpError) {
      const status = error.status;
      if (status === 401 || status === 403) {
        return new APIError(ErrorType.AUTHENTICATION_FAILED, 'Authentication failed. Please try again.', error);
      }
      if (status === 404) {
        return new APIError(ErrorType.STATION_NOT_FOUND, 'Station not found. Please check the station code and try again.', error);
      }
      if (status >= 500) {
        return new APIError(ErrorType.SERVICE_UNAVAILABLE, 'Service unavailable. Please try again later.', error);
      }
      return new APIError(ErrorType.INVALID_RESPONSE, `Request failed with status ${status}. Please try again.`, error);
    }

    // AbortController timeout or network failure
    if (error instanceof DOMException && error.name === 'AbortError') {
      return new APIError(ErrorType.NETWORK_ERROR, 'Request timeout. The server may be experiencing issues. Please try again.', error);
    }
    if (error instanceof TypeError) {
      return new APIError(ErrorType.NETWORK_ERROR, 'Connection failed. Please check your internet connection.', error);
    }

    return new APIError(
      ErrorType.NETWORK_ERROR,
      error instanceof Error ? error.message : 'Unknown error',
      error instanceof Error ? error : undefined
    );
  }


  /**
   * Fetch station data from metadata service (R2 storage)
   */
  private async fetchFromMetadataService(stationCode: string): Promise<WeatherStation> {
    const response = await this.plainFetch<{ success: boolean; station?: any }>(
      `${this.baseURL}/api/station-metadata`,
      { params: { stationCode }, timeout: 10000 }
    );
    
    if (!response.success || !response.station) {
      throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} not found in metadata service`, undefined);
    }
    
    const station = this.transformStationMetadata(response.station);
    if (station === null) {
      throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} has invalid coordinates and was filtered out`, undefined);
    }
    
    return station;
  }

  /**
   * Fetch station data from Weather API Proxy (primary source)
   */
  private async fetchFromWeatherProxy(stationCode: string): Promise<WeatherStation> {
    try {
      const stationData = await this.authFetch<any>(`/weatherstations/${stationCode}`);
      
      if (!stationData) {
        throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} not found in Weather API`, undefined);
      }
      
      const station = this.transformWeatherAPIResponse(stationData);
      if (station === null) {
        throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} has invalid coordinates and was filtered out`, undefined);
      }
      
      return station;
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.status === 503) {
          throw new APIError(ErrorType.SERVICE_UNAVAILABLE, 'Weather API is in offline mode', error);
        }
        if (error.status === 500) {
          throw new APIError(ErrorType.SERVICE_UNAVAILABLE, 'Weather API is misconfigured', error);
        }
        if (error.status === 404) {
          throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} not found in Weather API`, error);
        }
      }
      throw error;
    }
  }

  /**
   * Fetch all stations from Weather API Proxy
   */
  private async fetchAllFromWeatherProxy(): Promise<WeatherStation[]> {
    try {
      log.debug('Fetching all stations from Weather API proxy...');
      const data = await this.authFetch<any>('/weatherstations', { timeout: 60000 });
      
      log.debug('Weather API response type:', typeof data);
      
      if (data && data.error) {
        log.error('Weather API returned error:', data.error);
        throw new APIError(ErrorType.SERVICE_UNAVAILABLE, `Weather API error: ${data.error}`, undefined);
      }
      
      if (!data || !Array.isArray(data)) {
        log.warn('Weather API returned invalid data format');
        return [];
      }
      
      log.debug(`Weather API returned ${data.length} stations`);
      
      const transformedStations = data.map((stationData: any) => this.transformWeatherAPIResponse(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) {
        log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      }
      
      return stations;
    } catch (error) {
      log.error('fetchAllFromWeatherProxy error:', error);
      
      if (error instanceof HttpError) {
        log.error('HTTP error status:', error.status);
        if (error.status === 401) {
          throw new APIError(ErrorType.AUTHENTICATION_FAILED, 'Weather API authentication failed', error);
        }
        if (error.status === 503 || error.status === 500) {
          throw new APIError(ErrorType.SERVICE_UNAVAILABLE, 'Weather API is unavailable', error);
        }
      }
      throw error;
    }
  }

  /**
   * Transform Weather API response to WeatherStation format
   */
  private transformWeatherAPIResponse(stationData: any): WeatherStation | null {
    let latitude: number;
    let longitude: number;
    
    if (stationData.geoPoint) {
      latitude = this.parseNumericField(stationData.geoPoint.latitude, 0, 'geoPoint.latitude');
      longitude = this.parseNumericField(stationData.geoPoint.longitude, 0, 'geoPoint.longitude');
    } else {
      latitude = this.parseNumericField(stationData.latitude, 0, 'latitude');
      longitude = this.parseNumericField(stationData.longitude, 0, 'longitude');
    }
    
    if (!this.isValidCoordinate(latitude, longitude)) {
      return null;
    }
    
    const elevation = this.parseNumericField(stationData.elevation, 0, 'elevation');
    const devices = this.parseJSONField<any[]>(stationData.devices, 'devices', []);
    const transformedDevices = devices.map((d: any) => ({
      stationId: d.stationId || d.station_id || '',
      sourceStationId: d.sourceStationId || d.source_station_id || undefined,
      dataSourceId: d.dataSourceId || d.data_source_id || '',
      type: d.type || d.device || ''
    }));
    
    const tags = this.parseJSONField<Record<string, string>>(stationData.tags, 'tags', {});

    const isActive = stationData.active === true || stationData.active === 'true';

    const station: WeatherStation = {
      stationCode: stationData.stationCode || stationData.station_code || '',
      latitude,
      longitude,
      elevation,
      country: stationData.country || '',
      state: stationData.state || '',
      city: stationData.city || '',
      district: stationData.district || undefined,
      // Honest GADM unit types + stable GID codes, plus the structured admin[]
      // hierarchy — this proxy response carries them when upstream supplies them,
      // so they must be mapped through rather than dropped (was previously the
      // cause of a blank "Region Type"/"City" for station-code search results).
      stateType: stationData.stateType || stationData.state_type || undefined,
      cityType: stationData.cityType || stationData.city_type || undefined,
      districtType: stationData.districtType || stationData.district_type || undefined,
      countryCode: stationData.countryCode || stationData.country_code || undefined,
      stateCode: stationData.stateCode || stationData.state_code || undefined,
      cityCode: stationData.cityCode || stationData.city_code || undefined,
      districtCode: stationData.districtCode || stationData.district_code || undefined,
      admin: parseAdmin(stationData.admin),
      tags,
      firstObsTimestamp: stationData.firstObsTimestamp || stationData.first_obs_timestamp || '',
      lastObsTimestamp: stationData.lastObsTimestamp || stationData.last_obs_timestamp || '',
      startTime: stationData.startTime || stationData.start_time,
      devices: transformedDevices,
      isActive,
      isPrimaryResult: true,
      distance: stationData.distance !== undefined ? stationData.distance : undefined,
      parameters: Array.isArray(stationData.parameters) ? stationData.parameters : undefined,
      stationTier: getStationTier(transformedDevices),
    };
    
    return station;
  }

  /**
   * Fetch station data from Station Manager API
   */
  private async fetchFromStationManager(stationCode: string): Promise<WeatherStation> {
    const [details, deviceInfos] = await Promise.all([
      this.getStationDetails(stationCode),
      this.getStationDevices(stationCode).catch(() => [])
    ]);
    
    let country = '';
    let state = '';
    
    try {
      const geocoded = await GeocodingService.reverseGeocode(details.latitude, details.longitude);
      country = geocoded.country;
      state = geocoded.state;
    } catch (geocodeError) {
      // Geocoding is optional, continue without it
    }
    
    const station: WeatherStation = {
      stationCode: details.stationCode,
      latitude: details.latitude,
      longitude: details.longitude,
      elevation: details.elevation,
      country,
      state,
      tags: details.tags,
      firstObsTimestamp: details.firstObsTimestamp || details.lastObservation,
      lastObsTimestamp: details.lastObsTimestamp || details.lastObservation,
      startTime: undefined,
      devices: deviceInfos.map(d => ({
        stationId: d.stationId,
        dataSourceId: d.dataSourceId,
        type: d.type || d.device
      })),
      isActive: true,
      isPrimaryResult: true
    };
    
    return station;
  }

  /**
   * Get weather station by station code with cache-first strategy and fallback chain
   */
  async getStationByCode(stationCode: string): Promise<WeatherStation> {
    const startTime = Date.now();
    log.debug(`🔍 Fetching station: ${stationCode}`);

    // Check in-memory cache first
    if (this.cacheService) {
      const cached = this.cacheService.getByCode(stationCode);
      if (cached) {
        log.info(`✅ Cache hit: ${stationCode} (${Date.now() - startTime}ms)`);
        return cached;
      }
    }

    // Single source: AppRunner API via backend proxy
    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations`, {
        params: { stationCode },
        timeout: 30000,
      });

      const stationsArray = Array.isArray(data) ? data : [data];
      if (stationsArray.length === 0) {
        throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} not found`, undefined);
      }

      const station = this.transformWeatherAPIResponse(stationsArray[0]);
      if (!station) {
        throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} has invalid data`, undefined);
      }

      if (this.cacheService) {
        try { this.cacheService.upsert(station); } catch (_) { /* ignore */ }
      }

      const duration = Date.now() - startTime;
      log.info(`✅ Fetched ${stationCode} (${duration}ms)`);
      return station;
    } catch (error) {
      if (error instanceof APIError) throw error;
      const duration = Date.now() - startTime;
      log.error(`❌ Failed to fetch ${stationCode} (${duration}ms):`, error);
      throw new APIError(
        ErrorType.STATION_NOT_FOUND,
        `Station ${stationCode} not found`,
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get multiple weather stations by station codes with cache-first strategy
   */
  async getStationsByCodes(stationCodes: string[]): Promise<WeatherStation[]> {
    log.debug(`🔍 Fetching ${stationCodes.length} stations by codes`);
    const startTime = Date.now();

    let cachedStations: WeatherStation[] = [];
    let missingCodes: string[] = stationCodes;

    if (this.cacheService) {
      try {
        cachedStations = this.cacheService.getByCodes(stationCodes);
        const cachedCodes = new Set(cachedStations.map((s: WeatherStation) => s.stationCode));
        missingCodes = stationCodes.filter(code => !cachedCodes.has(code));
        log.debug(`Cache: ${cachedStations.length} found, ${missingCodes.length} missing`);
        if (missingCodes.length === 0) return cachedStations;
      } catch (error) {
        log.warn(`Cache lookup error:`, error);
        missingCodes = stationCodes;
      }
    }

    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations`, {
        params: { stationCode: missingCodes.join(',') },
        timeout: 120000,
      });

      const stationsArray = Array.isArray(data) ? data : data ? [data] : [];
      const fetchedStations = stationsArray
        .map((s: any) => this.transformWeatherAPIResponse(s))
        .filter((s: WeatherStation | null): s is WeatherStation => s !== null);

      if (this.cacheService && fetchedStations.length > 0) {
        try {
          fetchedStations.forEach((s: WeatherStation) => this.cacheService.upsert(s));
        } catch (_) { /* ignore */ }
      }

      const allStations = [...cachedStations, ...fetchedStations];
      const duration = Date.now() - startTime;
      log.info(`✅ Total: ${allStations.length} stations (${duration}ms)`);
      return allStations;
    } catch (error) {
      log.error(`❌ Batch fetch failed:`, error);
      return cachedStations;
    }
  }

  /**
   * Get weather stations near a specific station code
   */
  async getStationsNearStationCode(stationCode: string, radiusKm: number = 50): Promise<WeatherStation[]> {
    log.debug(`🎯 Optimized search: Finding stations near ${stationCode} within ${radiusKm}km`);
    
    try {
      const targetStation = await this.getStationByCode(stationCode);
      
      if (!targetStation.latitude || !targetStation.longitude) {
        throw new APIError(ErrorType.INVALID_RESPONSE, `Station ${stationCode} has no coordinates`, undefined);
      }
      
      log.debug(`📍 Target station coordinates: lat=${targetStation.latitude}, lon=${targetStation.longitude}`);
      
      const nearbyStations = await this.getStationsByCoordinates(
        targetStation.latitude,
        targetStation.longitude,
        radiusKm
      );
      
      log.info(`✅ Found ${nearbyStations.length} stations within ${radiusKm}km of ${stationCode}`);
      log.debug(`   Active stations: ${nearbyStations.filter(s => s.isActive).length}`);
      log.debug(`   Inactive stations: ${nearbyStations.filter(s => !s.isActive).length}`);
      
      return nearbyStations;
    } catch (error) {
      log.error(`❌ Failed to find stations near ${stationCode}:`, error);
      throw error;
    }
  }

  /**
   * Get all weather stations for map display
   */
  async getAllStationsForMap(): Promise<WeatherStation[]> {
    log.debug('🔄 Fetching all weather stations...');
    const startTime = Date.now();

    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations`, {
        timeout: 180000,
      });

      if (!data || !Array.isArray(data)) {
        log.warn('weatherstations returned non-array, returning empty');
        return [];
      }

      const stations = data
        .map((s: any) => this.transformWeatherAPIResponse(s))
        .filter((s: WeatherStation | null): s is WeatherStation => s !== null);

      const duration = Date.now() - startTime;
      const activeCount = stations.filter((s: WeatherStation) => s.isActive).length;
      log.info(`✅ Fetched ${stations.length} stations (${activeCount} active) in ${duration}ms`);

      return stations;
    } catch (error) {
      log.error('❌ Failed to fetch all stations:', error);
      return [];
    }
  }

  /**
   * Search stations by tags using Observations API
   */
  async searchStationsByTagsObservationsAPI(tagKey: string, tagValues: string[]): Promise<WeatherStation[]> {
    log.debug(`🔍 Searching Observations API for ${tagKey}: ${tagValues.join(', ')}`);
    
    try {
      const tags: Record<string, string[]> = {};
      if (tagKey && tagKey.trim() !== '') {
        tags[tagKey] = tagValues;
      } else {
        return [];
      }
      
      const response = await this.observationsAuthFetch<any>('/stations', {
        params: {
          by: 'tags',
          tags: JSON.stringify(tags)
        }
      });
      
      if (!response.features || response.features.length === 0) {
        log.debug(`   No stations found in Observations API`);
        return [];
      }
      
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      const stations: WeatherStation[] = response.features.map((feature: any) => {
        const props = feature.properties || {};
        const coords = feature.geometry?.coordinates || [0, 0];
        
        let isActive = false;
        if (props.lastObsTimestamp) {
          const lastObsDate = new Date(props.lastObsTimestamp);
          isActive = lastObsDate >= thirtyDaysAgo;
        }
        
        return {
          stationCode: feature.stationCode || '',
          latitude: coords[1],
          longitude: coords[0],
          elevation: props.elevation || 0,
          country: '',
          state: '',
          tags: props.tags || {},
          firstObsTimestamp: props.firstObsTimestamp || '',
          lastObsTimestamp: props.lastObsTimestamp || '',
          startTime: undefined,
          devices: [],
          isActive,
          isPrimaryResult: true
        };
      });
      
      return stations;
    } catch (error) {
      log.error(`❌ Observations API tag search failed:`, error instanceof Error ? error.message : error);
      return [];
    }
  }

  /**
   * Get weather stations by tags using Weather API as primary source
   */
  async getStationsByTags(tagKey: string, tagValues: string[]): Promise<WeatherStation[]> {
    log.debug(`Searching tags: key="${tagKey}", values=${tagValues.join(',')}`);
    const startTime = Date.now();

    const uniqueTagValues = [...new Set(
      tagValues.flatMap(v => String(v).split(/[,\s\t]+/).map(s => s.trim()).filter(Boolean))
    )];

    const allStations: WeatherStation[] = [];
    const seenCodes = new Set<string>();
    const isWildcard = !tagKey || tagKey.trim() === '';

    const searchPromises = uniqueTagValues.map(async (value) => {
      try {
        const url = isWildcard
          ? `${this.baseURL}/api/weather/weatherstations`
          : `${this.baseURL}/api/weather/weatherstations/tags`;

        const params = isWildcard
          ? { Tag: value }
          : { [tagKey]: value };

        const data = await this.plainFetch<any>(url, { params, timeout: 30000 });

        if (data && Array.isArray(data)) {
          for (const stationData of data) {
            const station = this.transformWeatherAPIResponse(stationData);
            if (station && !seenCodes.has(station.stationCode)) {
              seenCodes.add(station.stationCode);
              allStations.push(station);
            }
          }
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        log.warn(`⚠️ Tags search failed for value="${value}": ${msg}`);
        if (uniqueTagValues.length === 1) throw err;
      }
    });

    await Promise.allSettled(searchPromises);

    const duration = Date.now() - startTime;
    log.info(`✅ Tags search complete: ${allStations.length} stations (${duration}ms)`);
    return allStations;
  }

  /**
   * Get weather stations by coordinates (radius search) with distance calculation
   */
  async getStationsByCoordinates(latitude: number, longitude: number, radiusKm: number = 50): Promise<WeatherStation[]> {
    const startTime = Date.now();
    
    if (latitude == null || longitude == null || isNaN(latitude) || isNaN(longitude)) {
      throw new APIError(
        ErrorType.INVALID_RESPONSE,
        `Invalid coordinates provided: latitude=${latitude}, longitude=${longitude}. Coordinates must be valid numbers.`,
        undefined
      );
    }
    
    if (latitude < -90 || latitude > 90) {
      throw new APIError(ErrorType.INVALID_RESPONSE, `Invalid latitude: ${latitude}. Latitude must be between -90 and 90 degrees.`, undefined);
    }
    
    if (longitude < -180 || longitude > 180) {
      throw new APIError(ErrorType.INVALID_RESPONSE, `Invalid longitude: ${longitude}. Longitude must be between -180 and 180 degrees.`, undefined);
    }
    
    let validatedRadius = radiusKm;
    if (radiusKm == null || isNaN(radiusKm) || !isFinite(radiusKm)) {
      log.warn(`⚠️ Invalid radius value: ${radiusKm}, using default 50km`);
      validatedRadius = 50;
    } else if (radiusKm <= 0) {
      log.warn(`⚠️ Invalid radius value: ${radiusKm} (must be positive), using default 50km`);
      validatedRadius = 50;
    } else if (radiusKm > 20040) {
      log.warn(`⚠️ Radius ${radiusKm}km exceeds maximum (20,040km), capping at 20,040km`);
      validatedRadius = 20040;
    }
    
    log.debug(`🔍 Searching for stations near lat=${latitude}, lon=${longitude}, radius=${validatedRadius}km`);
    
    const radiusMeters = validatedRadius * 1000;
    
    // Attempt 1: Use weather proxy for coordinate search
    log.debug(`🔄 Attempt 1/2: Coordinate search via proxy`);
    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations/coordinates`, {
        params: {
          lat: latitude,
          lon: longitude,
          radius: radiusMeters,
          _t: Date.now()
        },
        timeout: 180000,
      });
      
      if (data && Array.isArray(data)) {
        log.debug(`Coordinate API returned ${data.length} stations`);
        
        const stations = data
          .map((stationData: any) => this.transformWeatherAPIResponse(stationData))
          .filter((station): station is WeatherStation => station !== null);
        
        for (const station of stations) {
          if (station.distance === undefined && this.isValidCoordinate(station.latitude, station.longitude)) {
            station.distance = Math.round(this.calculateDistance(latitude, longitude, station.latitude, station.longitude) * 100) / 100;
          }
        }
        
        stations.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
        
        const totalDuration = Date.now() - startTime;
        log.info(`✅ Coordinate search complete (${totalDuration}ms)`);
        log.debug(`   Found ${stations.length} stations within ${validatedRadius}km`);
        
        return stations;
      } else if (data && data.error) {
        log.error(`❌ API returned error: ${data.error}`);
        throw new Error(`Coordinate search API error: ${data.error}`);
      } else {
        log.error(`❌ Unexpected response format`);
        throw new Error('Coordinate search returned unexpected response format');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.warn(`⚠️ Coordinate search failed: ${errorMessage}`);
      if (error instanceof HttpError) {
        log.warn(`  Status: ${error.status}`);
      }
    }
    
    // Attempt 2: Fall back to in-memory cache
    log.debug(`🔄 Attempt 2/2: In-memory cache fallback`);
    
    if (this.cacheServicePromise) {
      try {
        await Promise.race([
          this.cacheServicePromise,
          new Promise((_, reject) => setTimeout(() => reject(new Error('Cache service load timeout')), 5000))
        ]);
      } catch (error) {
        log.warn(`⚠️ Cache service load timed out or failed`);
      }
    }
    
    if (this.cacheService && !this.cacheService.isReady()) {
      log.debug(`⏳ Cache not ready, skipping initialization (would be too slow)`);
    }
    
    if (this.cacheService && this.cacheService.isReady()) {
      log.debug(`🔄 Using in-memory cache for coordinate search`);
      const cacheStartTime = Date.now();
      
      try {
        const cachedResults = this.cacheService.getByCoordinates(latitude, longitude, validatedRadius);
        const cacheDuration = Date.now() - cacheStartTime;
        const totalDuration = Date.now() - startTime;
        
        log.info(`✅ Cache coordinate search complete`);
        log.debug(`   Found ${cachedResults.length} stations within ${validatedRadius}km`);
        if (cachedResults.length > 0) {
          log.debug(`   Closest station: ${cachedResults[0].stationCode} at ${cachedResults[0].distance?.toFixed(2)}km`);
        }
        log.debug(`   Cache search time: ${cacheDuration}ms`);
        log.debug(`   Total time: ${totalDuration}ms`);
        
        const activeCount = cachedResults.filter((s: WeatherStation) => s.isActive).length;
        const inactiveCount = cachedResults.length - activeCount;
        log.debug(`   Active stations: ${activeCount}`);
        log.debug(`   Inactive stations: ${inactiveCount}`);
        
        return cachedResults;
      } catch (cacheError) {
        log.warn(`⚠️ Cache coordinate search failed:`, cacheError);
      }
    } else {
      log.debug(`⚠️ Cache not ready for coordinate search`);
    }
    
    const totalDuration = Date.now() - startTime;
    log.error(`❌ Coordinate search failed - all sources unavailable (${totalDuration}ms)`);
    
    throw new APIError(
      ErrorType.SERVICE_UNAVAILABLE,
      'Station data is currently unavailable. Please try again later.',
      undefined
    );
  }


  private parseNumericField(value: any, defaultValue: number = 0, fieldName?: string): number {
    if (value === null || value === undefined || value === '') return defaultValue;
    if (typeof value === 'number') return isNaN(value) ? defaultValue : value;
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
      if (fieldName) log.warn(`⚠️ Failed to parse numeric field '${fieldName}': ${value}, using default ${defaultValue}`);
      return defaultValue;
    }
    return parsed;
  }

  private parseJSONField<T>(value: any, fieldName: string, defaultValue: T): T {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === 'object') return value as T;
    if (typeof value === 'string') {
      if (value.trim() === '') return defaultValue;
      try {
        return JSON.parse(value) as T;
      } catch (e) {
        log.warn(`⚠️ Failed to parse JSON field '${fieldName}': ${e instanceof Error ? e.message : 'Unknown error'}`);
        return defaultValue;
      }
    }
    log.warn(`⚠️ Unexpected type for field '${fieldName}': ${typeof value}, using default`);
    return defaultValue;
  }

  private transformStationMetadata(stationData: any): WeatherStation | null {
    if (!stationData || typeof stationData !== 'object') {
      log.error('transformStationMetadata: Invalid stationData (not an object)');
      throw new Error('Invalid station data: stationData must be an object');
    }
    
    const stationCode = APIResponseValidator.safeStringExtract(
      stationData.station_code || stationData.stationCode, 'stationCode', ''
    );
    
    if (!stationCode || stationCode.trim() === '') {
      log.warn('transformStationMetadata: Missing or empty stationCode');
      log.warn('Station data:', {
        station_code: stationData.station_code,
        stationCode: stationData.stationCode,
        hasStationCode: !!stationData.station_code || !!stationData.stationCode
      });
    }
    
    const country = APIResponseValidator.safeStringExtract(stationData.country, 'country', '');
    const state = APIResponseValidator.safeStringExtract(stationData.state, 'state', '');
    
    const latitude = this.parseNumericField(stationData.latitude, 0, 'latitude');
    const longitude = this.parseNumericField(stationData.longitude, 0, 'longitude');
    const elevation = this.parseNumericField(stationData.elevation, 0, 'elevation');
    
    if (!this.isValidCoordinate(latitude, longitude)) {
      let reason = '';
      if (latitude === 0 && longitude === 0) reason = 'placeholder (0, 0)';
      else if (latitude === -90 || latitude === 90) reason = 'pole coordinate';
      else if (longitude === 0 && Math.abs(latitude) < 10) reason = 'suspicious longitude=0';
      else reason = 'unknown';
      
      const count = this.invalidCoordinatesCount.get(reason) || 0;
      this.invalidCoordinatesCount.set(reason, count + 1);
      
      if (count === 0) {
        log.warn(
          `Station ${stationCode} has invalid coordinates: ` +
          `lat=${latitude}, lon=${longitude} (${reason}) - further similar warnings suppressed`
        );
      }
      return null;
    }
    
    const devicesArray = this.parseJSONField<any[]>(stationData.devices, 'devices', []);
    const devices = devicesArray.map((device: any) => {
      if (!device || typeof device !== 'object') {
        log.warn('transformStationMetadata: Invalid device object, skipping');
        return { stationId: '', dataSourceId: '', type: 'Unknown' };
      }
      return {
        stationId: APIResponseValidator.safeStringExtract(device.station_id || device.stationId, 'device.stationId', ''),
        sourceStationId: APIResponseValidator.safeStringExtract(device.source_station_id || device.sourceStationId, 'device.sourceStationId', undefined),
        dataSourceId: APIResponseValidator.safeStringExtract(device.data_source_id || device.dataSourceId || device.network, 'device.dataSourceId', ''),
        type: APIResponseValidator.safeStringExtract(device.network_type || device.networkType || device.ObsType || device.obs_type || device.type || device.network, 'device.type', 'Unknown')
      };
    });
    
    const tags = this.parseJSONField<Record<string, string>>(stationData.tags, 'tags', {});
    
    const firstObsTimestamp = APIResponseValidator.safeStringExtract(
      stationData.first_obs_timestamp || stationData.firstObsTimestamp, 'firstObsTimestamp', ''
    );
    const lastObsTimestamp = APIResponseValidator.safeStringExtract(
      stationData.last_obs_timestamp || stationData.lastObsTimestamp, 'lastObsTimestamp', ''
    );
    const startTime = stationData.start_time || stationData.startTime || undefined;
    const isActive = stationData.active === true || stationData.active === 'true';
    
    if (!stationCode) {
      log.warn('transformStationMetadata: Station missing stationCode');
    }
    if (latitude === 0 || longitude === 0) {
      const reason = 'zero-coordinate';
      const count = this.invalidCoordinatesCount.get(reason) || 0;
      this.invalidCoordinatesCount.set(reason, count + 1);
      if (count === 0) {
        log.warn('transformStationMetadata: Station has invalid coordinates:', {
          stationCode, latitude, longitude, note: 'Further similar warnings suppressed'
        });
      }
    }
    
    return {
      stationCode,
      latitude,
      longitude,
      elevation,
      country,
      state,
      tags,
      firstObsTimestamp,
      lastObsTimestamp,
      startTime,
      devices,
      isActive,
      isPrimaryResult: true,
      parameters: Array.isArray(stationData.parameters) ? stationData.parameters : undefined
    };
  }

  private isValidCoordinate(latitude: number, longitude: number): boolean {
    if (latitude === 0 && longitude === 0) return false;
    if (latitude === -90 || latitude === 90) return false;
    if (longitude === 0 && Math.abs(latitude) < 10) return false;
    return true;
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private extractStationsArray(metadata: any): any[] {
    if (metadata.stations && Array.isArray(metadata.stations)) return metadata.stations;
    if (Array.isArray(metadata)) return metadata;
    for (const key of Object.keys(metadata)) {
      const value = metadata[key];
      if (Array.isArray(value)) return value;
    }
    throw new APIError(ErrorType.INVALID_RESPONSE, 'Metadata does not contain stations array', undefined);
  }

  /**
   * Get weather stations by country using Weather API as primary source
   */
  async getStationsByCountry(country: string): Promise<WeatherStation[]> {
    log.debug(`🌍 Fetching stations for country: ${country}`);
    const startTime = Date.now();
    
    // Attempt 1: Weather API via backend proxy
    log.debug(`🔄 Attempt 1/2: Weather API country search`);
    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations`, {
        params: { Country: country },
        timeout: 120000,
      });
      
      if (data && Array.isArray(data)) {
        log.debug(`Weather API returned ${data.length} stations for ${country}`);
        
        const transformedStations = data.map((stationData: any) => this.transformWeatherAPIResponse(stationData));
        const stations: WeatherStation[] = transformedStations.filter(
          (station): station is WeatherStation => station !== null
        );
        
        const filteredCount = transformedStations.length - stations.length;
        if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
        
        const activeCount = stations.filter(s => s.isActive).length;
        log.debug(`   Active: ${activeCount}, Inactive: ${stations.length - activeCount}`);
        
        const duration = Date.now() - startTime;
        log.info(`✅ Weather API country search complete (${duration}ms)`);
        return stations;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.warn(`⚠️ Weather API country search failed: ${errorMessage}`);
    }
    
    // Attempt 2: R2 Metadata (FALLBACK)
    log.debug(`🔄 Attempt 2/2: R2 metadata fallback`);
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const countryLower = country.toLowerCase();
      const matchingStations = stationsData.filter((station: any) => {
        return station.country && station.country.toLowerCase() === countryLower;
      });
      
      log.info(`✅ Found ${matchingStations.length} stations in ${country}`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const activeCount = stations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${stations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return stations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations for ${country}:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get weather stations by country and state
   */
  async getStationsByCountryAndState(country: string, state: string): Promise<WeatherStation[]> {
    log.debug(`🌍 Fetching stations for ${country}/${state}`);
    
    if (!state || state.trim() === '') return this.getStationsByCountry(country);
    
    const startTime = Date.now();
    
    // Attempt 1: Weather API via backend proxy
    log.debug(`🔄 Attempt 1/2: Weather API state search`);
    try {
      const data = await this.plainFetch<any>(`${this.baseURL}/api/weather/weatherstations`, {
        params: { State: state },
        timeout: 120000,
      });
      
      if (data && Array.isArray(data)) {
        log.debug(`Weather API returned ${data.length} stations for state ${state}`);
        
        const transformedStations = data.map((stationData: any) => this.transformWeatherAPIResponse(stationData));
        const stations: WeatherStation[] = transformedStations.filter(
          (station): station is WeatherStation => station !== null
        );
        
        const filteredCount = transformedStations.length - stations.length;
        if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
        
        const activeCount = stations.filter(s => s.isActive).length;
        log.debug(`   Active: ${activeCount}, Inactive: ${stations.length - activeCount}`);
        
        const duration = Date.now() - startTime;
        log.info(`✅ Weather API state search complete (${duration}ms)`);
        return stations;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      log.warn(`⚠️ Weather API state search failed: ${errorMessage}`);
    }
    
    // Attempt 2: R2 Metadata (FALLBACK)
    log.debug(`🔄 Attempt 2/2: R2 metadata fallback`);
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const countryLower = country.toLowerCase();
      const stateLower = state.toLowerCase();
      const matchingStations = stationsData.filter((station: any) => {
        const matchesCountry = station.country && station.country.toLowerCase() === countryLower;
        const matchesState = station.state && station.state.toLowerCase() === stateLower;
        return matchesCountry && matchesState;
      });
      
      log.info(`✅ Found ${matchingStations.length} stations in ${country}/${state}`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const activeCount = stations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${stations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return stations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations for ${country}/${state}:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get weather stations by multiple countries using R2 metadata
   */
  async getStationsByCountries(countries: string[]): Promise<WeatherStation[]> {
    log.debug(`🌍 Fetching stations for ${countries.length} countries:`, countries);
    const startTime = Date.now();
    
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const countriesSet = new Set(countries.map(c => c.toLowerCase()));
      const matchingStations = stationsData.filter((station: any) => {
        const stationCountry = station.country ? station.country.toLowerCase() : '';
        return countriesSet.has(stationCountry);
      });
      
      log.info(`✅ Found ${matchingStations.length} matching stations`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const uniqueStations = new Map<string, WeatherStation>();
      stations.forEach(station => {
        if (!uniqueStations.has(station.stationCode)) uniqueStations.set(station.stationCode, station);
      });
      
      const deduplicatedStations = Array.from(uniqueStations.values());
      log.info(`✅ Returning ${deduplicatedStations.length} unique stations across ${countries.length} countries`);
      
      const activeCount = deduplicatedStations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${deduplicatedStations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return deduplicatedStations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations for countries:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get weather stations by multiple countries and states using R2 metadata
   */
  async getStationsByCountriesAndStates(filters: Map<string, Set<string>>): Promise<WeatherStation[]> {
    const countries = Array.from(filters.keys());
    const totalStates = Array.from(filters.values()).reduce((sum, states) => sum + states.size, 0);
    
    log.debug(`🌍 Fetching stations for ${countries.length} countries and ${totalStates} states`);
    const startTime = Date.now();
    
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const countryFilters = new Set<string>();
      const stateFilters = new Map<string, Set<string>>();
      
      filters.forEach((states, country) => {
        const countryLower = country.toLowerCase();
        if (states.size === 0) {
          countryFilters.add(countryLower);
        } else {
          const statesLower = new Set(Array.from(states).map(s => s.toLowerCase()));
          stateFilters.set(countryLower, statesLower);
        }
      });
      
      const matchingStations = stationsData.filter((station: any) => {
        const stationCountry = station.country ? station.country.toLowerCase() : '';
        const stationState = station.state ? station.state.toLowerCase() : '';
        
        if (countryFilters.has(stationCountry)) return true;
        const statesForCountry = stateFilters.get(stationCountry);
        if (statesForCountry && statesForCountry.has(stationState)) return true;
        return false;
      });
      
      log.info(`✅ Found ${matchingStations.length} matching stations`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const uniqueStations = new Map<string, WeatherStation>();
      stations.forEach(station => {
        if (!uniqueStations.has(station.stationCode)) uniqueStations.set(station.stationCode, station);
      });
      
      const deduplicatedStations = Array.from(uniqueStations.values());
      log.info(`✅ Returning ${deduplicatedStations.length} unique stations (deduplicated from ${stations.length})`);
      
      const activeCount = deduplicatedStations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${deduplicatedStations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return deduplicatedStations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations for countries/states:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get weather stations by states using R2 metadata
   */
  async getStationsByStates(states: Map<string, Set<string>>): Promise<WeatherStation[]> {
    const countries = Array.from(states.keys());
    const totalStates = Array.from(states.values()).reduce((sum, stateSet) => sum + stateSet.size, 0);
    
    log.debug(`🌍 Fetching stations for ${totalStates} states across ${countries.length} countries`);
    const startTime = Date.now();
    
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const stateFilters = new Map<string, Set<string>>();
      states.forEach((stateSet, country) => {
        const countryLower = country.toLowerCase();
        const statesLower = new Set(Array.from(stateSet).map(s => s.toLowerCase()));
        stateFilters.set(countryLower, statesLower);
      });
      
      const matchingStations = stationsData.filter((station: any) => {
        const stationCountry = station.country ? station.country.toLowerCase() : '';
        const stationState = station.state ? station.state.toLowerCase() : '';
        if (!stationCountry || !stationState) return false;
        const statesForCountry = stateFilters.get(stationCountry);
        if (!statesForCountry) return false;
        return statesForCountry.has(stationState);
      });
      
      log.info(`✅ Found ${matchingStations.length} matching stations`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const uniqueStations = new Map<string, WeatherStation>();
      stations.forEach(station => {
        if (!uniqueStations.has(station.stationCode)) uniqueStations.set(station.stationCode, station);
      });
      
      const deduplicatedStations = Array.from(uniqueStations.values());
      log.info(`✅ Returning ${deduplicatedStations.length} unique stations (deduplicated from ${stations.length})`);
      
      const activeCount = deduplicatedStations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${deduplicatedStations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return deduplicatedStations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations by states:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Get weather stations by cities using R2 metadata
   */
  async getStationsByCities(cities: Map<string, Map<string, Set<string>>>): Promise<WeatherStation[]> {
    const countries = Array.from(cities.keys());
    let totalCities = 0;
    cities.forEach(stateMap => { stateMap.forEach(citySet => { totalCities += citySet.size; }); });
    
    log.debug(`🌍 Fetching stations for ${totalCities} cities across ${countries.length} countries`);
    const startTime = Date.now();
    
    try {
      const response = await this.plainFetch<{ success: boolean; metadata?: any }>(
        `${this.baseURL}/api/station-metadata`, { timeout: 30000 }
      );
      
      if (!response.success || !response.metadata) {
        throw new APIError(ErrorType.INVALID_RESPONSE, 'Invalid metadata response', undefined);
      }
      
      const stationsData = this.extractStationsArray(response.metadata);
      log.debug(`📊 Searching ${stationsData.length} stations in metadata`);
      
      const cityFilters = new Map<string, Map<string, Set<string>>>();
      cities.forEach((stateMap, country) => {
        const countryLower = country.toLowerCase();
        const stateFiltersForCountry = new Map<string, Set<string>>();
        stateMap.forEach((citySet, state) => {
          const stateLower = state.toLowerCase();
          const citiesLower = new Set(Array.from(citySet).map(c => c.toLowerCase()));
          stateFiltersForCountry.set(stateLower, citiesLower);
        });
        cityFilters.set(countryLower, stateFiltersForCountry);
      });
      
      const matchingStations = stationsData.filter((station: any) => {
        const stationCountry = station.country ? station.country.toLowerCase() : '';
        const stationState = station.state ? station.state.toLowerCase() : '';
        if (!stationCountry || !stationState) return false;
        
        let stationCity = '';
        if (station.tags) {
          let tags: Record<string, any> = {};
          if (typeof station.tags === 'string') {
            try { tags = JSON.parse(station.tags); } catch (e) { return false; }
          } else if (typeof station.tags === 'object') {
            tags = station.tags;
          }
          const cityValue = tags.city || tags.City;
          if (cityValue && typeof cityValue === 'string') {
            stationCity = cityValue.toLowerCase();
          } else {
            return false;
          }
        } else {
          return false;
        }
        
        const statesForCountry = cityFilters.get(stationCountry);
        if (!statesForCountry) return false;
        const citiesForState = statesForCountry.get(stationState);
        if (!citiesForState) return false;
        return citiesForState.has(stationCity);
      });
      
      log.info(`✅ Found ${matchingStations.length} matching stations`);
      
      const transformedStations = matchingStations.map((stationData: any) => this.transformStationMetadata(stationData));
      const stations: WeatherStation[] = transformedStations.filter(
        (station): station is WeatherStation => station !== null
      );
      
      const filteredCount = transformedStations.length - stations.length;
      if (filteredCount > 0) log.debug(`Filtered ${filteredCount} stations with invalid coordinates`);
      
      const uniqueStations = new Map<string, WeatherStation>();
      stations.forEach(station => {
        if (!uniqueStations.has(station.stationCode)) uniqueStations.set(station.stationCode, station);
      });
      
      const deduplicatedStations = Array.from(uniqueStations.values());
      log.info(`✅ Returning ${deduplicatedStations.length} unique stations (deduplicated from ${stations.length})`);
      
      const activeCount = deduplicatedStations.filter(s => s.isActive).length;
      log.debug(`   Active: ${activeCount}, Inactive: ${deduplicatedStations.length - activeCount}`);
      
      const duration = Date.now() - startTime;
      log.debug(`   Search completed in ${duration}ms`);
      return deduplicatedStations;
    } catch (error) {
      log.error(`❌ Failed to fetch stations by cities:`, error);
      if (error instanceof APIError) throw error;
      throw new APIError(
        ErrorType.SERVICE_UNAVAILABLE,
        'Unable to load station data. The metadata service is currently unavailable.',
        error instanceof Error ? error : undefined
      );
    }
  }

  extractCountries(stations: WeatherStation[]): string[] {
    const countries = new Set<string>();
    stations.forEach(station => { if (station.country) countries.add(station.country); });
    const sortedCountries = Array.from(countries).sort();
    log.info(`📊 Extracted ${sortedCountries.length} unique countries`);
    return sortedCountries;
  }

  extractStates(stations: WeatherStation[], country: string): string[] {
    const states = new Set<string>();
    stations
      .filter(station => station.country === country)
      .forEach(station => { if (station.state && station.state !== 'NA') states.add(station.state); });
    const sortedStates = Array.from(states).sort();
    log.info(`📊 Extracted ${sortedStates.length} unique states for ${country}`);
    return sortedStates;
  }

  /**
   * Get station details from Station Manager API
   */
  async getStationDetails(stationCode: string): Promise<import('../types/stationManager').StationDetails> {
    log.debug(`🔍 Fetching station details for: ${stationCode}`);
    
    try {
      const data = await this.stationManagerAuthFetch<import('../types/stationManager').StationManagerStation[]>(
        `/stations`,
        { params: { station_code: stationCode } }
      );

      if (!data || data.length === 0) {
        throw new APIError(ErrorType.STATION_NOT_FOUND, `Station ${stationCode} not found in Station Manager`, undefined);
      }

      const station = data[0];
      
      const details: import('../types/stationManager').StationDetails = {
        stationName: station.tags.name || stationCode,
        stationCode: station.station_code,
        latitude: parseFloat(station.latitude),
        longitude: parseFloat(station.longitude),
        elevation: parseFloat(station.elevation),
        lastObservation: station.last_observation_reported,
        firstObsTimestamp: station.first_obs_timestamp,
        lastObsTimestamp: station.last_obs_timestamp,
        tags: station.tags as Record<string, string>
      };

      log.info(`✅ Station details retrieved: ${details.stationName}`);
      return details;
    } catch (error) {
      log.error(`❌ Failed to fetch station details for ${stationCode}:`, error);
      throw error;
    }
  }

  /**
   * Get multiple station details from Station Manager API in parallel
   */
  async getMultipleStationDetails(stationCodes: string[]): Promise<Map<string, import('../types/stationManager').StationDetails>> {
    log.debug(`🔍 Fetching details for ${stationCodes.length} stations in parallel`);
    
    const promises = stationCodes.map(code => 
      this.getStationDetails(code)
        .then(details => ({ code, details, success: true as const }))
        .catch(error => {
          log.warn(`⚠️ Failed to fetch ${code} from Station Manager:`, error.message);
          return { code, error, success: false as const };
        })
    );

    const results = await Promise.all(promises);
    
    const detailsMap = new Map<string, import('../types/stationManager').StationDetails>();
    results.forEach(result => {
      if (result.success) detailsMap.set(result.code, result.details);
    });

    log.info(`✅ Successfully fetched ${detailsMap.size} out of ${stationCodes.length} stations from Station Manager`);
    return detailsMap;
  }

  /**
   * Get devices for a station from Station Manager API
   */
  async getStationDevices(stationCode: string): Promise<import('../types/stationManager').DeviceInfo[]> {
    log.debug(`🔍 Fetching devices for station: ${stationCode}`);
    
    try {
      const data = await this.stationManagerAuthFetch<import('../types/stationManager').StationManagerDevice[]>(
        `/devices`,
        { params: { station_code: stationCode } }
      );

      // @ts-ignore - JavaScript module without type definitions
      const { getNetworkType } = await import('../../lib/network-type-mapping.js');
      
      const devices: import('../types/stationManager').DeviceInfo[] = data.map(device => ({
        device: device.network,
        stationId: device.station_name,
        sourceStationId: device.source_station_id,
        dataSourceId: device.network,
        type: getNetworkType(device.network)
      }));

      log.info(`✅ Found ${devices.length} devices for station ${stationCode}`);
      return devices;
    } catch (error) {
      log.error(`❌ Failed to fetch devices for ${stationCode}:`, error);
      throw error;
    }
  }

  /**
   * Get devices for multiple stations from Station Manager API in parallel
   */
  async getMultipleStationDevices(stationCodes: string[]): Promise<Map<string, import('../types/stationManager').DeviceInfo[]>> {
    log.debug(`🔍 Fetching devices for ${stationCodes.length} stations in parallel`);
    
    const promises = stationCodes.map(code => 
      this.getStationDevices(code)
        .then(devices => ({ code, devices, success: true as const }))
        .catch(error => {
          log.warn(`⚠️ Failed to fetch devices for ${code}:`, error.message);
          return { code, error, success: false as const };
        })
    );

    const results = await Promise.all(promises);
    
    const devicesMap = new Map<string, import('../types/stationManager').DeviceInfo[]>();
    results.forEach(result => {
      if (result.success) devicesMap.set(result.code, result.devices);
    });

    log.info(`✅ Successfully fetched devices for ${devicesMap.size} out of ${stationCodes.length} stations`);
    return devicesMap;
  }

  /**
   * Get observation timestamps for multiple stations from Observations API
   */
  async getObservationTimestamps(stationCodes: string[]): Promise<Map<string, { firstObsTimestamp?: string; lastObsTimestamp?: string }>> {
    log.debug(`🔍 Fetching observation timestamps for ${stationCodes.length} stations`);
    
    const timestampsMap = new Map<string, { firstObsTimestamp?: string; lastObsTimestamp?: string }>();
    if (stationCodes.length === 0) return timestampsMap;
    
    const batchSize = 50;
    
    for (let i = 0; i < stationCodes.length; i += batchSize) {
      const batch = stationCodes.slice(i, i + batchSize);
      const stationCodesParam = batch.join(',');
      
      try {
        const data = await this.observationsAuthFetch<any>('/observations/stations', {
          params: {
            stationCodes: stationCodesParam,
            by: 'stationCodes',
            showTags: 'true'
          }
        });
        
        if (data?.features && Array.isArray(data.features)) {
          data.features.forEach((feature: any) => {
            const stationCode = feature.stationCode;
            const properties = feature.properties || {};
            if (stationCode) {
              timestampsMap.set(stationCode, {
                firstObsTimestamp: properties.firstObsTimestamp,
                lastObsTimestamp: properties.lastObsTimestamp
              });
            }
          });
        }
      } catch (error) {
        log.warn(`⚠️ Failed to fetch timestamps for batch of ${batch.length} stations:`, error);
      }
    }
    
    log.info(`✅ Successfully fetched timestamps for ${timestampsMap.size} out of ${stationCodes.length} stations`);
    return timestampsMap;
  }
}

// Export singleton instance
export const weatherStationAPI = new WeatherStationAPI(authService);
